import { createFileRoute, Link } from "@tanstack/react-router";
import { differenceInCalendarDays, format, formatDistanceToNowStrict } from "date-fns";
import {
  ArrowRight,
  ArrowUpRight,
  Activity,
  Target,
  AlertOctagon,
  HelpCircle,
  GitBranch,
  Users,
  CalendarClock,
} from "lucide-react";

import { useCurrentPhase, usePhasesList } from "@/application/use-cases/phases/use-phases.queries";
import { useRisksList } from "@/application/use-cases/risks/use-risks.queries";
import { useDecisionsList } from "@/application/use-cases/decisions/use-decisions.queries";
import { useOpenQuestionsList } from "@/application/use-cases/open-questions/use-open-questions.queries";
import { useMeetingsList } from "@/application/use-cases/meetings/use-meetings.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import { WeekStrip } from "@/presentation/components/timeline/week-strip";
import type {
  RiskRow,
  DecisionRow,
  OpenQuestionRow,
  MeetingRow,
  PhaseRow,
  StakeholderRow,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/")({
  component: DashboardPage,
});

function DashboardPage() {
  const phase = useCurrentPhase();
  const phasesList = usePhasesList();
  const risks = useRisksList();
  const decisions = useDecisionsList();
  const questions = useOpenQuestionsList();
  const meetings = useMeetingsList();
  const stakeholders = useStakeholdersList();

  const data = {
    phase: phase.data,
    phaseLoading: phase.isLoading,
    phases: phasesList.data ?? [],
    risks: risks.data ?? [],
    decisions: decisions.data ?? [],
    questions: questions.data ?? [],
    meetings: meetings.data ?? [],
    stakeholders: stakeholders.data ?? [],
  };

  const openRisks = data.risks.filter((r) => r.status === "open" || r.status === "active");
  const reds = openRisks.filter((r) => r.rag === "red");
  const ambers = openRisks.filter((r) => r.rag === "amber");
  const greens = openRisks.filter((r) => r.rag === "green");
  const liveQuestions = data.questions.filter((q) => q.status !== "resolved");
  const openDecisions = data.decisions.filter((d) => d.status !== "resolved");
  const lockedDecisions = data.decisions.filter((d) => d.status === "resolved");
  const upcomingMeetings = data.meetings
    .filter((m) => new Date(m.meeting_at) >= new Date())
    .slice(0, 3);

  return (
    <div className="animate-page-enter mx-auto flex min-h-full w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="dashboard" />
      </div>
      <Hero
        phase={data.phase}
        phaseLoading={data.phaseLoading}
        totalPhases={data.phases.length}
        stakeholderCount={data.stakeholders.length}
      />

      <section className="stagger-children grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Critical Risks"
          icon={AlertOctagon}
          value={reds.length}
          hint={`${ambers.length} amber · ${greens.length} green`}
          tone="red"
          href="/risks"
        />
        <StatCard
          label="Open Questions"
          icon={HelpCircle}
          value={liveQuestions.length}
          hint="awaiting decision"
          tone="amber"
          href="/open-questions"
        />
        <StatCard
          label="Decisions"
          icon={GitBranch}
          value={openDecisions.length}
          hint={`${lockedDecisions.length} locked`}
          tone="cyan"
          href="/decisions"
        />
        <StatCard
          label="Stakeholders"
          icon={Users}
          value={data.stakeholders.length}
          hint="mapped this cycle"
          tone="green"
          href="/stakeholders"
        />
      </section>

      <main className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <RiskShowpiece
            risks={openRisks}
            stakeholders={data.stakeholders}
            redsCount={reds.length}
            ambersCount={ambers.length}
            greensCount={greens.length}
          />
        </section>

        <aside className="flex flex-col gap-6">
          <DecisionsBlock decisions={data.decisions} />
          <QuestionsBlock questions={data.questions} />
          <MeetingsBlock meetings={upcomingMeetings} />
        </aside>
      </main>

      <footer className="glow-card-static flex flex-col gap-5 p-6 sm:p-7">
        <div className="flex items-baseline justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="text-primary size-4" />
            <span className="text-sm font-semibold tracking-tight">Discovery Spine</span>
          </div>
          <span className="font-data text-muted-foreground text-[11px] tracking-[0.16em] uppercase">
            4-Week Assessment
          </span>
        </div>
        {data.phases.length > 0 ? (
          <WeekStrip phases={data.phases} currentNumber={data.phase?.number ?? 1} />
        ) : null}
      </footer>
    </div>
  );
}

/* ============================================================
 * Hero — paper canvas, large headline, gate marker
 * ============================================================ */

function Hero({
  phase,
  phaseLoading,
  totalPhases,
  stakeholderCount,
}: {
  phase: PhaseRow | null | undefined;
  phaseLoading: boolean;
  totalPhases: number;
  stakeholderCount: number;
}) {
  const today = new Date();
  const daysToGate = phase ? differenceInCalendarDays(endOfWeekFor(phase.week_of), today) : null;
  const totalLabel = totalPhases > 0 ? String(totalPhases).padStart(2, "0") : "04";

  return (
    <div className="hero-panel relative overflow-hidden rounded-2xl">
      <div className="scanlines pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative grid gap-8 p-7 sm:p-9 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-10 lg:p-10">
        {/* Left: identity + headline */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-[oklch(0.72_0.17_190_/_0.30)] bg-[oklch(0.72_0.17_190_/_0.10)] px-3 py-1 text-[11px] font-medium text-[oklch(0.85_0.15_190)]">
              <Activity className="size-3" />
              <span className="font-data tracking-[0.12em] uppercase">Concierge OS · Live</span>
            </span>
            <span className="font-data text-[10px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.55)] uppercase">
              Yango B2B Loyalty · Discovery
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-end gap-5">
              {phase ? (
                <h1 className="font-data text-[140px] leading-[0.85] font-medium tracking-[-0.04em] text-[oklch(0.93_0.008_240)] sm:text-[170px]">
                  {String(phase.number).padStart(2, "0")}
                </h1>
              ) : (
                <div
                  aria-hidden
                  className={`h-[120px] w-[150px] rounded-md border border-[oklch(1_0_0_/_0.06)] bg-[oklch(1_0_0_/_0.02)] sm:h-[145px] sm:w-[180px] ${
                    phaseLoading ? "animate-pulse" : ""
                  }`}
                />
              )}
              <div className="flex flex-col gap-1.5 pb-5">
                <span className="eyebrow-bright">Week</span>
                <span className="font-data text-[20px] leading-none font-medium text-[oklch(0.85_0.01_240_/_0.55)]">
                  / {totalLabel}
                </span>
              </div>
            </div>

            {phase ? (
              <div className="flex max-w-2xl flex-col gap-2">
                <h2 className="text-[26px] leading-[1.15] font-bold tracking-tight text-[oklch(0.97_0.005_240)] sm:text-[32px]">
                  {phase.title}.
                </h2>
                <p className="text-[14px] leading-relaxed text-[oklch(0.85_0.01_240_/_0.70)]">
                  {phase.focus}
                </p>
              </div>
            ) : (
              <p className="text-[20px] font-light text-[oklch(0.85_0.01_240_/_0.45)]">
                {phaseLoading ? "Loading the week…" : "No active phase yet."}
              </p>
            )}
          </div>

          <p className="font-data text-[11px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.45)] uppercase">
            {format(today, "EEEE · d MMM yyyy")}
          </p>
        </div>

        {/* Right: gate + meta */}
        <div className="flex flex-col gap-4">
          {phase ? (
            <div className="rounded-xl border border-[oklch(1_0_0_/_0.10)] bg-[oklch(1_0_0_/_0.04)] p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <StatusDot tone={phase.status} size="lg" ring pulse />
                  <span className="font-data text-[10px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.55)] uppercase">
                    Thursday Gate
                  </span>
                </div>
                {daysToGate !== null ? (
                  <span className="font-data text-[10px] tracking-[0.16em] text-[oklch(0.85_0.01_240_/_0.50)] uppercase">
                    {daysToGate <= 0 ? "Due Now" : `T-${daysToGate}d`}
                  </span>
                ) : null}
              </div>
              <p className="mt-3 text-[18px] leading-snug font-semibold text-[oklch(0.97_0.005_240)]">
                {phase.gate}
              </p>
              <div
                aria-hidden
                className="pointer-events-none mt-4 h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, oklch(0.72 0.17 190 / 0.55) 50%, transparent)",
                }}
              />
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <MetaTile
              icon={Users}
              label="Stakeholders"
              value={String(stakeholderCount)}
              hint="mapped"
            />
            <MetaTile icon={Target} label="Vision" value="White-label" hint="loyalty super-app" />
          </div>

          <Link
            to="/timeline"
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="flex items-center gap-2">
              <CalendarClock className="size-3.5 text-[oklch(0.72_0.17_190)]" />
              Inspect roadmap
            </span>
            <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MetaTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[oklch(1_0_0_/_0.08)] bg-[oklch(1_0_0_/_0.03)] px-3.5 py-3">
      <div className="flex items-center gap-1.5">
        <Icon className="size-3 text-[oklch(0.72_0.17_190)]" />
        <span className="font-data text-[9.5px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.55)] uppercase">
          {label}
        </span>
      </div>
      <span className="font-data text-[18px] leading-none font-semibold text-[oklch(0.97_0.005_240)]">
        {value}
      </span>
      <span className="text-[10.5px] text-[oklch(0.85_0.01_240_/_0.55)]">{hint}</span>
    </div>
  );
}

/* ============================================================
 * Stat cards — KPI strip
 * ============================================================ */

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
  href,
}: {
  label: string;
  value: number;
  hint: string;
  icon: React.ElementType;
  tone: "red" | "amber" | "green" | "cyan";
  href?: string;
}) {
  const toneClass = {
    red: "text-[var(--rag-red)] bg-[oklch(0.62_0.22_25_/_0.08)] ring-[oklch(0.62_0.22_25_/_0.20)]",
    amber:
      "text-[var(--rag-amber)] bg-[oklch(0.74_0.16_80_/_0.08)] ring-[oklch(0.74_0.16_80_/_0.22)]",
    green:
      "text-[var(--rag-green)] bg-[oklch(0.66_0.16_155_/_0.08)] ring-[oklch(0.66_0.16_155_/_0.20)]",
    cyan: "text-[var(--primary)] bg-[oklch(0.55_0.17_190_/_0.08)] ring-[oklch(0.55_0.17_190_/_0.22)]",
  }[tone];

  const inner = (
    <div className="glow-card group flex h-full flex-col gap-3 p-5">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <div className={`flex size-7 items-center justify-center rounded-md ring-1 ${toneClass}`}>
          <Icon className="size-3.5" />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-data text-[40px] leading-none font-semibold tracking-tight ${
            value === 0 ? "text-muted-foreground" : "text-foreground"
          }`}
        >
          {value}
        </span>
        {href ? (
          <ArrowRight className="text-muted-foreground/40 group-hover:text-foreground ml-auto size-3.5 transition-all group-hover:translate-x-0.5" />
        ) : null}
      </div>
      <span className="text-muted-foreground text-[11.5px]">{hint}</span>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block">
        {inner}
      </Link>
    );
  }
  return inner;
}

/* ============================================================
 * Risk Showpiece — primary panel
 * ============================================================ */

function RiskShowpiece({
  risks,
  stakeholders,
  redsCount,
  ambersCount,
  greensCount,
}: {
  risks: RiskRow[];
  stakeholders: StakeholderRow[];
  redsCount: number;
  ambersCount: number;
  greensCount: number;
}) {
  const ownersById = new Map(stakeholders.map((s) => [s.id, s.name]));
  const reds = risks.filter((r) => r.rag === "red");
  const ambers = risks.filter((r) => r.rag === "amber");

  return (
    <section className="glow-card flex h-full flex-col gap-6 p-6 sm:p-7">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="eyebrow">Live Risk Register</span>
          <h2 className="text-[22px] leading-tight font-bold tracking-tight">
            Where the cycle is <span className="text-[var(--rag-red)]">tense</span>.
          </h2>
        </div>
        <Link
          to="/risks"
          className="group font-data text-muted-foreground hover:text-primary inline-flex shrink-0 items-center gap-1 text-[10.5px] tracking-[0.16em] uppercase transition-colors print:hidden"
        >
          Full register
          <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </header>

      {/* Severity bar */}
      <div className="bg-muted flex h-1.5 overflow-hidden rounded-full">
        {redsCount > 0 ? (
          <div style={{ flex: redsCount, background: "var(--rag-red)" }} className="h-full" />
        ) : null}
        {ambersCount > 0 ? (
          <div style={{ flex: ambersCount, background: "var(--rag-amber)" }} className="h-full" />
        ) : null}
        {greensCount > 0 ? (
          <div style={{ flex: greensCount, background: "var(--rag-green)" }} className="h-full" />
        ) : null}
        {redsCount + ambersCount + greensCount === 0 ? <div className="h-full flex-1" /> : null}
      </div>

      <div className="flex flex-col gap-1">
        {reds.length === 0 && ambers.length === 0 ? (
          <EmptyLine>The register opens calm. Reds will surface as we map the systems.</EmptyLine>
        ) : null}

        {reds.map((r) => (
          <RiskRowCritical
            key={r.id}
            risk={r}
            owner={r.owner_id ? ownersById.get(r.owner_id) : null}
          />
        ))}

        {ambers.length > 0 ? (
          <div className="mt-5 flex flex-col gap-2">
            <span className="eyebrow">Amber · watching</span>
            <ul className="flex flex-col">
              {ambers.map((r) => (
                <RiskRowAmber
                  key={r.id}
                  risk={r}
                  owner={r.owner_id ? ownersById.get(r.owner_id) : null}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function RiskRowCritical({ risk, owner }: { risk: RiskRow; owner?: string | null }) {
  return (
    <Link
      to="/risks"
      search={{ id: risk.id }}
      className="group border-border hover:bg-accent relative flex items-start gap-4 rounded-lg border-t px-2 py-3.5 transition-colors first:border-t-0 print:break-inside-avoid"
    >
      <span
        aria-hidden
        className="absolute top-1/2 left-0 h-0 w-[3px] -translate-y-1/2 rounded-full bg-[var(--rag-red)] transition-all group-hover:h-[60%]"
      />
      <span className="mt-1.5 shrink-0">
        <StatusDot tone={risk.rag} size="lg" ring pulse />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2.5">
          <span className="font-data text-[10px] font-semibold tracking-[0.16em] text-[var(--rag-red)] uppercase">
            {risk.code}
          </span>
          <h3 className="text-[14.5px] leading-snug font-medium">{risk.title}</h3>
        </div>
        {risk.mitigation ? (
          <p className="text-muted-foreground line-clamp-2 text-[12.5px] leading-relaxed print:line-clamp-none">
            {risk.mitigation}
          </p>
        ) : null}
      </div>
      <div className="shrink-0 pl-2 text-right">
        <p className="text-[12.5px] font-medium">{owner ?? "—"}</p>
        <p className="font-data text-muted-foreground text-[9.5px] tracking-[0.16em] uppercase">
          {STATUS_LABEL[risk.status]}
        </p>
      </div>
    </Link>
  );
}

function RiskRowAmber({ risk, owner }: { risk: RiskRow; owner?: string | null }) {
  return (
    <li className="border-border flex items-baseline gap-3 border-t py-2 first:border-t-0 print:break-inside-avoid">
      <StatusDot tone="amber" size="sm" />
      <Link
        to="/risks"
        search={{ id: risk.id }}
        className="hover:text-primary flex flex-1 items-baseline justify-between gap-3 transition-colors"
      >
        <span className="text-[13px]">
          <span className="font-data text-muted-foreground text-[10px] tracking-[0.14em] uppercase">
            {risk.code}
          </span>
          <span className="ml-2">{risk.title}</span>
        </span>
        <span className="text-muted-foreground text-[12px]">{owner ?? "—"}</span>
      </Link>
    </li>
  );
}

/* ============================================================
 * Side blocks — smaller glow cards
 * ============================================================ */

function DecisionsBlock({ decisions }: { decisions: DecisionRow[] }) {
  const open = decisions.filter((d) => d.status !== "resolved");
  const resolved = decisions.filter((d) => d.status === "resolved");

  return (
    <section className="glow-card flex flex-col gap-4 p-5">
      <BlockHeader label="Decisions" href="/decisions" tone="cyan">
        <Tally label="open" value={open.length} tone="cyan" />
        <Tally label="locked" value={resolved.length} />
      </BlockHeader>

      <ul className="flex flex-col">
        {open.length === 0 && resolved.length === 0 ? (
          <EmptyLine>
            Nothing locked yet — first decision expected at Thursday's WMS gate.
          </EmptyLine>
        ) : null}
        {open.slice(0, 4).map((d) => (
          <li
            key={d.id}
            className="border-border flex items-baseline gap-3 border-t py-2.5 first:border-t-0 print:break-inside-avoid"
          >
            <StatusDot tone={d.status} size="sm" />
            <Link
              to="/decisions"
              search={{ id: d.id }}
              className="hover:text-primary flex-1 text-[13px] transition-colors"
            >
              {d.title}
            </Link>
            <span className="font-data text-muted-foreground text-[10px] tracking-[0.14em] uppercase">
              {d.gate ?? STATUS_LABEL[d.status]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function QuestionsBlock({ questions }: { questions: OpenQuestionRow[] }) {
  const live = questions.filter((q) => q.status !== "resolved");
  const oldest = live[0];

  return (
    <section className="glow-card relative flex flex-col gap-4 overflow-hidden p-5">
      <BlockHeader label="Questions" href="/open-questions" tone="amber">
        <Tally label="awaiting" value={live.length} tone="amber" />
      </BlockHeader>

      {live.length === 0 ? (
        <EmptyLine>
          No open questions yet. They surface as we hold stakeholder interviews.
        </EmptyLine>
      ) : (
        <div className="flex flex-col gap-3">
          {oldest ? (
            <div className="flex flex-col gap-2 rounded-md border-l-2 border-[var(--rag-amber)] bg-[oklch(0.74_0.16_80_/_0.06)] px-3.5 py-3">
              <p className="text-[14px] leading-snug">{oldest.question}</p>
            </div>
          ) : null}
          {live.length > 1 ? (
            <p className="text-muted-foreground text-[11.5px]">
              + {live.length - 1} more · oldest{" "}
              <span className="font-data">
                {formatDistanceToNowStrict(new Date(oldest!.created_at), { addSuffix: true })}
              </span>
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

function MeetingsBlock({ meetings }: { meetings: MeetingRow[] }) {
  return (
    <section className="glow-card flex flex-col gap-4 p-5">
      <BlockHeader label="Meetings" href="/meetings" tone="green">
        <Tally label="upcoming" value={meetings.length} tone="green" />
      </BlockHeader>

      <ul className="flex flex-col">
        {meetings.length === 0 ? (
          <EmptyLine>No meetings booked yet — kickoff is the first.</EmptyLine>
        ) : (
          meetings.map((m) => (
            <li
              key={m.id}
              className="border-border flex items-baseline gap-3 border-t py-2.5 first:border-t-0 print:break-inside-avoid"
            >
              <span className="font-data text-[10px] tracking-[0.14em] text-[var(--rag-green)] uppercase">
                {format(new Date(m.meeting_at), "EEE")}
              </span>
              <Link
                to="/meetings/$id"
                params={{ id: m.id }}
                className="hover:text-primary flex-1 text-[13px] transition-colors"
              >
                {m.title}
              </Link>
              <span className="font-data text-muted-foreground text-[11.5px]">
                {format(new Date(m.meeting_at), "d MMM")}
              </span>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}

/* ============================================================
 * Primitives
 * ============================================================ */

function BlockHeader({
  label,
  href,
  tone: _tone,
  children,
}: {
  label: string;
  href: string;
  tone: "cyan" | "amber" | "green";
  children?: React.ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3">
      <div className="border-border flex items-baseline justify-between border-b pb-3">
        <span className="text-sm font-semibold tracking-tight">{label}</span>
        <Link
          to={href}
          className="group font-data text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase transition-colors print:hidden"
        >
          Open
          <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
      {children ? <div className="flex items-baseline gap-5">{children}</div> : null}
    </header>
  );
}

function Tally({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "cyan" | "amber" | "green";
}) {
  const toneClass = tone
    ? {
        cyan: "text-primary",
        amber: "text-[var(--rag-amber)]",
        green: "text-[var(--rag-green)]",
      }[tone]
    : "text-foreground";

  return (
    <div className="flex items-baseline gap-2">
      <span
        className={`font-data text-[28px] leading-none font-semibold ${
          value === 0 ? "text-muted-foreground/60" : toneClass
        }`}
      >
        {value}
      </span>
      <span className="font-data text-muted-foreground text-[10px] tracking-[0.16em] uppercase">
        {label}
      </span>
    </div>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground text-[13.5px] leading-snug">{children}</p>;
}

function endOfWeekFor(weekOf: string): Date {
  const start = new Date(weekOf);
  return new Date(start.getTime() + 4 * 24 * 60 * 60 * 1000); // Sun → Thu (Israeli workweek)
}
